import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Eye,
  EyeOff,
  Loader2,
  ShieldCheck,
  ArrowLeft,
  ArrowUpRight,
} from "lucide-react";
import LeadModal from "../components/public/LeadModal";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pinPhase, setPinPhase] = useState(false);
  const [pinToken, setPinToken] = useState("");
  const [pinUserMeta, setPinUserMeta] = useState({ role: "", name: "" });
  const [leadOpen, setLeadOpen] = useState(false);
  const { login, completePinLogin } = useAuth();
  const navigate = useNavigate();

  const detectType = (v) => {
    const s = (v || "").trim();
    if (/^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(s)) return "Client (PAN)";
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) return "Super Admin";
    if (s.length) return "Associate";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const resp = await axios.post(`${API_URL}/auth/login`, {
        login_id: loginId,
        password,
      });
      if (resp.data?.requires_pin) {
        setPinToken(resp.data.pin_token);
        setPinUserMeta({
          role: resp.data.user_role,
          name: resp.data.user_name,
        });
        setPinPhase(true);
        toast.info("Enter your 4-digit security PIN to continue");
      } else {
        await login(loginId, password);
        toast.success("Welcome back!");
        navigate(popRedirect() || "/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const popRedirect = () => {
    const r = sessionStorage.getItem("post_login_redirect");
    sessionStorage.removeItem("post_login_redirect");
    return r;
  };

  const handleVerifyPin = async (e) => {
    e.preventDefault();
    if (!pin || pin.length < 4) {
      toast.error("Enter a 4-digit PIN");
      return;
    }
    setLoading(true);
    try {
      const user = await completePinLogin(pinToken, pin);
      toast.success(`Welcome back, ${user?.name || "user"}!`);
      const role = user?.role;
      if (role === "client") navigate("/portfolio");
      else if (role === "sub_broker") navigate(popRedirect() || "/client-wise");
      else navigate(popRedirect() || "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid PIN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-[100dvh] bg-[#07111F] text-white grid lg:grid-cols-12"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* ─── Left branding pane ─── */}
      <aside
        data-testid="login-branding"
        className="relative hidden lg:flex lg:col-span-5 flex-col justify-between px-12 py-14 overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-32 h-[500px] w-[500px] rounded-full bg-[#C8A96B]/10 blur-[140px]" />
          <div className="absolute bottom-0 right-0 h-[400px] w-[400px] rounded-full bg-[#1a3f3a]/30 blur-[160px]" />
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        <Link to="/" className="relative inline-flex items-center gap-3 group">
          <span className="text-white tracking-[0.18em] text-sm font-medium">
            KINNTEGRA
          </span>
          <span className="h-[6px] w-[6px] rounded-full bg-[#C8A96B] opacity-80" />
        </Link>

        <div className="relative">
          <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/40 mb-6">
            <span className="h-[1px] w-8 bg-[#C8A96B]/60" />
            Welcome back
          </div>
          <h2 className="text-4xl xl:text-5xl font-light tracking-[-0.025em] leading-[1.1]">
            Beyond
            <br />
            <span className="italic text-[#C8A96B]">portfolio reporting.</span>
          </h2>
          <p className="mt-6 text-base text-white/60 max-w-md leading-relaxed font-light">
            Sign in to access your firm's reporting, operations,
            continuity, and succession infrastructure.
          </p>
        </div>

        <div className="relative text-xs text-white/40 flex items-center justify-between">
          <span>© {new Date().getFullYear()} Kinntegra</span>
          <Link
            to="/"
            className="hover:text-white/70 inline-flex items-center gap-1"
          >
            <ArrowLeft size={12} /> Back to site
          </Link>
        </div>
      </aside>

      {/* ─── Right form pane ─── */}
      <section className="lg:col-span-7 flex items-center justify-center px-6 md:px-10 py-12 md:py-16 bg-[#0a1626]">
        <div className="w-full max-w-md" data-testid="login-form-wrap">
          {/* Mobile-only logo */}
          <Link
            to="/"
            className="lg:hidden mb-10 inline-flex items-center gap-3"
          >
            <span className="text-white tracking-[0.18em] text-sm font-medium">
              KINNTEGRA
            </span>
            <span className="h-[6px] w-[6px] rounded-full bg-[#C8A96B]" />
          </Link>

          {!pinPhase ? (
            <>
              <h1
                data-testid="login-headline"
                className="text-3xl md:text-4xl font-light tracking-[-0.02em]"
              >
                Sign in.
              </h1>
              <p className="mt-3 text-sm text-white/55">
                Enter your credentials to access your account.
              </p>

              <form
                onSubmit={handleSubmit}
                className="mt-10 space-y-6"
                data-testid="login-form"
              >
                <Field
                  label="Login ID"
                  type="text"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  placeholder="Email · Associate Code · PAN"
                  testId="login-email"
                  autoComplete="username"
                  hint={loginId ? `Detected: ${detectType(loginId)}` : null}
                  required
                  disabled={loading}
                />

                <div>
                  <label className="block">
                    <span className="text-xs text-white/55 tracking-wide">
                      Password{" "}
                      <span className="text-[#C8A96B]">*</span>
                    </span>
                    <div className="relative mt-2">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={loading}
                        autoComplete="current-password"
                        data-testid="login-password"
                        className="block w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 pr-11 text-white placeholder:text-white/30 focus:outline-none focus:border-[#C8A96B]/60 focus:bg-white/[0.06] transition"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition"
                        tabIndex={-1}
                        data-testid="login-password-toggle"
                        aria-label="Toggle password visibility"
                      >
                        {showPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowUpRight size={16} />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-5 flex items-center justify-center gap-4 text-xs text-white/50">
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-password-link"
                  className="hover:text-[#C8A96B] transition"
                >
                  Forgot password?
                </Link>
                <span className="text-white/15">·</span>
                <Link
                  to="/forgot-login-id"
                  data-testid="login-forgot-loginid-link"
                  className="hover:text-[#C8A96B] transition"
                >
                  Forgot login ID?
                </Link>
              </div>

              <div className="mt-6 text-sm text-white/55 text-center">
                New to Kinntegra?{" "}
                <button
                  type="button"
                  onClick={() => setLeadOpen(true)}
                  data-testid="login-connect-link"
                  className="text-[#C8A96B] hover:underline"
                >
                  Connect with us
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#C8A96B] mb-4">
                <ShieldCheck size={14} /> Two-factor verification
              </div>
              <h1 className="text-3xl md:text-4xl font-light tracking-[-0.02em]">
                Enter your PIN.
              </h1>
              <p className="mt-3 text-sm text-white/55">
                Hi {pinUserMeta.name || "there"} — confirm your 4-digit security PIN to continue.
              </p>

              <form
                onSubmit={handleVerifyPin}
                className="mt-10 space-y-6"
                data-testid="login-pin-form"
              >
                <label className="block">
                  <span className="text-xs text-white/55 tracking-wide">
                    Security PIN
                  </span>
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={pin}
                    onChange={(e) =>
                      setPin(e.target.value.replace(/\D/g, ""))
                    }
                    required
                    disabled={loading}
                    autoFocus
                    data-testid="login-pin"
                    placeholder="••••"
                    className="mt-2 block w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-4 text-center text-3xl tracking-[0.4em] text-white placeholder:text-white/20 focus:outline-none focus:border-[#C8A96B]/60 transition"
                  />
                </label>

                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setPinPhase(false);
                      setPin("");
                    }}
                    data-testid="login-pin-back"
                    className="inline-flex items-center justify-center gap-1 px-4 py-3 rounded-full border border-white/15 text-white/80 hover:bg-white/5 transition"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="login-pin-submit"
                    className="col-span-2 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Verifying…
                      </>
                    ) : (
                      <>Verify & Sign In</>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </section>
      {leadOpen && (
        <LeadModal onClose={() => setLeadOpen(false)} source="login_page" />
      )}
    </div>
  );
};

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  testId,
  required,
  hint,
  disabled,
  autoComplete,
}) {
  return (
    <label className="block">
      <span className="text-xs text-white/55 tracking-wide">
        {label} {required && <span className="text-[#C8A96B]">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        data-testid={testId}
        className="mt-2 block w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-[#C8A96B]/60 focus:bg-white/[0.06] transition"
      />
      {hint && (
        <p className="mt-1.5 text-[11px] text-white/40">{hint}</p>
      )}
    </label>
  );
}

export default Login;
